/**
 * "Power-ups you keep" on the profile page: what this account has won, and one sentence saying
 * where it goes.
 *
 * **NOTHING HERE IS PRESSABLE, AND THAT IS THE CARD RATHER THAN AN OMISSION** — spending one is
 * not a press at all. What a player is carrying is debited where their WebSocket ticket is signed
 * and lands in their inventory the moment their body spawns, so a "use" control here would be a
 * button that lies about a decision nobody makes on this page. That is the rule the rejected-photo
 * tile in the skin gallery is under. The lobby is where it is said out loud, in one line — see
 * `components/lobby/carrying-line.tsx`.
 *
 * **THE PRESENTATION IS THE REGISTRY'S AND THIS FILE RESTATES NONE OF IT.** The name, the
 * description and the glyph arrive on the row, resolved server-side through `POWER_UPS` — the one
 * place a power-up is described, and the same list the HUD's inventory strip and the how-to-play
 * page read. So a tenth power-up appears here the day it is registered, and the string `'rocket'`
 * appears in this file exactly nowhere.
 *
 * **A guest is drawn the card and told why it is empty.** `powerups.list` is a `protectedProcedure`
 * that refuses a guest with a `FORBIDDEN`, so the query is not *made* on their behalf rather than
 * made and shown as an error — the arrangement the photo library and the sayings picker are
 * already under. In practice the profile page hands a guest its own upgrade card instead, so this
 * is the belt: a card that renders honestly whoever mounts it.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { trpc } from '@/lib/trpc';

export function PowerUpsCard({ isGuest }: { isGuest: boolean }) {
  const carried = trpc.powerups.list.useQuery(undefined, { enabled: !isGuest });

  const items = carried.data?.items ?? [];
  const maxPerKind = carried.data?.maxPerKind ?? 0;

  return (
    <Card data-testid="powerups-card">
      <CardHeader>
        <CardTitle>Power-ups you keep</CardTitle>
        <CardDescription>
          Win a deathmatch round and you keep a power-up. It goes into your next match with you.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isGuest ? (
          <p className="text-sm text-muted-foreground">
            Guests do not keep power-ups between matches — a guest account lives on this device
            alone. Create an account and what you win follows you.
          </p>
        ) : carried.error ? (
          <p className="text-sm text-destructive" role="alert">
            {carried.error.message}
          </p>
        ) : carried.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing yet. Be the last one standing in a deathmatch room and you will be carrying one.
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.kind} className="flex items-start gap-3">
                {/* The glyph the registry gave it, drawn the way the inventory strip draws it —
                    decoration beside the name rather than the label itself, so a screen reader
                    reads the power-up's name and not an emoji. */}
                <span aria-hidden="true" className="text-2xl leading-none">
                  {item.glyph}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold">
                    {item.name}
                    <span className="ml-2 font-normal tabular-nums text-muted-foreground">
                      &times;{item.count}
                      {item.count >= maxPerKind && maxPerKind > 0 && ' — full'}
                    </span>
                  </p>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
